import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";

export type RegionDto = {
  id: number;
  name: string;
  divisionId?: number;
};

export async function getRegions(divisionId: number): Promise<RegionDto[]> {
  const localApi = await getLocalApi();
  const path = (apiConfig as any).region_get_all.replace("{divisionId}", String(divisionId));
  const list = await localApi.fetchList(
    path,
    (dto: any) => ({
      id: dto.id,
      name: dto.name ?? "",
      divisionId: dto.divisionId ?? divisionId,
    }),
    "Unable to fetch regions"
  );
  return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

function parseRegionErrorResponse(text: string, fallback: string): string {
  let message = fallback;
  try {
    const json = text ? JSON.parse(text) : {};
    if (json?.message) message = json.message;
    if (json?.errors?.length) message = json.errors[0];
    if (typeof json === "string") message = json;
  } catch {

  }
  return message;
}

export async function createRegion(
  name: string,
  divisionId: number
): Promise<{ id: number; name: string; divisionId?: number }> {
  const localApi = await getLocalApi();
  const path = (apiConfig as any).region_create;
  const body = JSON.stringify({ name, divisionId });
  const res = await localApi.call(path, "POST", body);
  if (!res?.ok) {
    const text = await res?.text().catch(() => "");
    throw new Error(parseRegionErrorResponse(text, "Failed to create region"));
  }
  const data = await res.json().catch(() => ({}));
  return {
    id: data.id ?? data.regionId,
    name: data.name ?? name,
    divisionId: data.divisionId ?? divisionId,
  };
}

export async function updateRegion(id: number, name: string): Promise<void> {
  const localApi = await getLocalApi();
  const path = (apiConfig as any).region_update;
  const res = await localApi.call(path, "PUT", JSON.stringify({ id, name }));
  if (!res?.ok) {
    const text = await res?.text().catch(() => "");
    throw new Error(parseRegionErrorResponse(text, "Failed to update region"));
  }
}

export type SaveCountryRegionsResponse = {
  divisionId: number;
  allCountriesAssigned: boolean;
  unassignedCountires: Array<{ countryId: number; countryCode: string; countryName: string }>;
};

export async function saveCountryRegions(
  divisionId: number,
  countryRegions: Array<{ countryId: number; regionId: number }>
): Promise<SaveCountryRegionsResponse> {
  const localApi = await getLocalApi();
  const path = (apiConfig as any).region_create_regions_for_countries;
  const res = await localApi.call(
    path,
    "POST",
    JSON.stringify({ divisionId, countryRegions })
  );
  if (!res?.ok) {
    const text = await res?.text().catch(() => "");
    throw new Error(text || "Failed to save country regions");
  }
  return res.json();
}

export async function getDivisionInheritance(
  divisionId: number
): Promise<{ divisionId: number; inheritedFromDivisionId: number | null }> {
  const localApi = await getLocalApi();
  const path = (apiConfig as any).region_get_inheritance.replace(
    "{divisionId}",
    String(divisionId)
  );
  const res = await localApi.call(path, "GET", undefined);
  if (!res?.ok) {
    const text = await res?.text().catch(() => "");
    throw new Error(parseRegionErrorResponse(text, "Failed to load inheritance"));
  }
  const data = await res.json().catch(() => ({}));
  return {
    divisionId: data.divisionId ?? divisionId,
    inheritedFromDivisionId:
      data.inheritedFromDivisionId != null ? data.inheritedFromDivisionId : null,
  };
}

export async function setDivisionInheritance(
  divisionId: number,
  inheritedFromDivisionId: number | null
): Promise<void> {
  const localApi = await getLocalApi();
  const path = (apiConfig as any).region_set_inheritance.replace(
    "{divisionId}",
    String(divisionId)
  );
  const res = await localApi.call(
    path,
    "PUT",
    JSON.stringify({ inheritedFromDivisionId })
  );
  if (!res?.ok) {
    const text = await res?.text().catch(() => "");
    throw new Error(parseRegionErrorResponse(text, "Failed to save inheritance"));
  }
}